/**
 * FichaPersonaView -- #/persona/{id}, #/persona/colaborador/{id}, #/persona/usuario/{id}.
 * Mision SUJETO-PERSONA-V1-001 / SUJ-T8 bloque 1 (va de Josse #17380, maqueta #17343).
 *
 * AUDIENCIA: el dueno o el administrativo del negocio, y el admin de plataforma (Josse), en
 * escritorio. La API recorta a los negocios de quien pregunta (PersonasController): el dueno de
 * un negocio solo ve las membresias y fichas de los suyos.
 *
 * UNA sola ficha por persona, abierta igual desde Personas, Colaboradores y Usuarios. Cada lista
 * entra con el id que ya tiene; la ficha hace UNA consulta (GET de persona por esa via). La
 * cabecera y "como va este mes" salen de esa respuesta. El detalle de cada negocio (asistencia
 * por clase, creditos, capacitacion) se pide SOLO al abrirlo.
 *
 * PETICIONES-POR-MONTAJE: 1. Al abrir una tarjeta (bloque 2), una vez: asistencia 1 por negocio de
 * sus fichas; danos 1 (persona/danos); creditos 1 (persona/creditos). Al abrir el detalle de un negocio: 4 (el colaborador y sus tres
 * secciones), una sola vez por negocio. Al abrir "Cursando ahora" (bloque 4), una vez: pensum 1,
 * perfil 1 por programa del pensum (el 404 es "no esta en ese programa") y temas 1 por programa con perfil.
 *
 * Reglas en application/personas/fichaUnica.js (sin DOM) y fichaDePersona.js.
 *
 * @module presentation/views/FichaPersonaView
 */

import { PersonaApiClient } from '../../infrastructure/api/PersonaApiClient.js';
import { ColaboradorApiClient } from '../../infrastructure/api/ColaboradorApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { leerPersona, porQueNoHayPersona } from '../../application/personas/fichaDePersona.js';
import { leerRutaDeLaFicha, cabeceraDeLaPersona, tarjetasDelMes, fichasPorNegocio } from '../../application/personas/fichaUnica.js';
import { porQueNoHayDetalle, danosPorDia, creditosPorNegocio, titularDeCreditos, diasDeAsistencia, claseEnPalabras, pesosCOP } from '../../application/personas/detalleDeLaFicha.js';
import { AsistenciaApiClient } from '../../infrastructure/api/AsistenciaApiClient.js';
import { filasDelDetector, filasDelColaborador } from '../../application/colaboradores/asistenciaDeLaFicha.js';
import { puedeCorregir, marcasDelDia, horaBogota, diaBogota, correccionDe } from '../../application/personas/correccionDeMarcaje.js';
import { AppConfig } from '../../config.js';
import { programasDelPensum, skillActual, cursandoAhora, porQueNoCursaNada } from '../../application/personas/cursandoAhora.js';
import { LearningApiClient } from '../../infrastructure/api/LearningApiClient.js';
import { LearningProfileApiClient } from '../../infrastructure/api/LearningProfileApiClient.js';
import { lineaDePin, puedeCambiarPin, PARA_QUE_SIRVE_EL_PIN } from '../../application/personas/pinDeLaFicha.js';
import { negociosSinFicha, cuerpoDeLaFicha, fraseDelResultado, AVISO_DEL_CARGO } from '../../application/personas/fichaDeOperacion.js';
import { datosDelColaborador, cuerpoDeDatos, fraseDeDatos } from '../../application/personas/datosDeLaFicha.js';
import { formatearFecha } from '../shared/fechaColombiana.js';
import { MESES_MES_A_MES, mesesHacia, asistenciaDelMes, leerMesAMes, tablaDelNegocio, textoDeCelda, TITULOS, avisoDeLaApi } from '../../application/personas/mesAMes.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
const CSS = `
  .fp { max-width:1040px; }
  .fp-cabeza { display:flex; gap:16px; align-items:center; flex-wrap:wrap; padding:14px 0 12px; border-bottom:1px solid #30363d; }
  .fp-avatar { width:52px; height:52px; border-radius:50%; background:#1f6feb33; color:#79c0ff; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:1.1rem; flex:0 0 auto; }
  .fp-nombre { margin:0; font-size:1.2rem; color:#f0f6fc; }
  .fp-chips { display:flex; flex-wrap:wrap; gap:6px; margin-top:6px; }
  .fp-chip { font-size:0.875rem; padding:3px 9px; border-radius:12px; border:1px solid #30363d; color:#8b949e; }
  .fp-chip.ok { border-color:#3fb95066; color:#3fb950; }
  .fp-h2 { font-size:0.875rem; color:#8b949e; text-transform:uppercase; letter-spacing:.05em; margin:18px 0 8px; }
  .fp-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:10px; }
  .fp-card { background:#161b22; border:1px solid #21262d; border-left:3px solid #3fb950; border-radius:9px; padding:11px 13px; }
  .fp-card.apagada { border-left-color:#30363d; opacity:.65; }
  .fp-card .t { font-size:0.875rem; color:#8b949e; text-transform:uppercase; letter-spacing:.04em; }
  .fp-card .v { font-size:1.2rem; font-weight:700; margin-top:5px; color:#e6edf3; }
  .fp-card .d { font-size:0.875rem; color:#8b949e; margin-top:3px; line-height:1.4; }
  .fp-card .f { display:block; margin-top:7px; font-size:0.875rem; color:#6e7681; font-family:ui-monospace, Consolas, monospace; }
  .fp-card .x { font-size:0.875rem; color:#d29922; margin-top:4px; }
  .fp-negocio { background:#0d1117; border:1px solid #21262d; border-radius:9px; padding:8px 12px; margin-bottom:8px; }
  .fp-negocio summary { cursor:pointer; font-size:0.875rem; color:#e6edf3; }
  .fp-negocio[open] summary { margin-bottom:8px; }
  .fp-abre { margin-top:8px; border-top:1px dashed #30363d; padding-top:6px; }
  .fp-abre summary { cursor:pointer; font-size:0.875rem; color:#79c0ff; }
  .fp-det { width:100%; border-collapse:collapse; margin-top:6px; font-size:0.875rem; }
  .fp-det td { padding:3px 4px; border-bottom:1px solid #21262d; color:#8b949e; }
  .fp-det td.n { text-align:right; color:#e6edf3; white-space:nowrap; }
  .fp-mes th { text-align:left; padding:3px 4px; font-size:0.875rem; color:#c9d1d9; border-bottom:1px solid #30363d; }
  .fp-mes td:first-child { color:#e6edf3; white-space:nowrap; }
  @media (max-width: 600px) {
    .fp-mes thead { display:none; }
    .fp-mes tr { display:block; padding:6px 0; border-bottom:1px solid #21262d; }
    .fp-mes td { display:flex; justify-content:space-between; gap:12px; border:0; padding:2px 0; }
    .fp-mes td:first-child { font-weight:600; }
    .fp-mes td[data-l]::before { content: attr(data-l); color:#8b949e; }
    .fp-mes td[data-l] { text-align:right; }
  }
  .fp-sub { font-size:0.875rem; color:#e6edf3; margin-top:8px; }
  .fp-nada { font-size:0.875rem; color:#8b949e; margin:6px 0 0; line-height:1.4; }
  .fp-cls { font-size:0.875rem; padding:1px 5px; border-radius:5px; border:1px solid #30363d; }
  .fp-pin { display:flex; flex-wrap:wrap; align-items:center; gap:6px 10px; font-size:0.875rem; color:#e6edf3; padding:6px 0; border-bottom:1px solid #21262d; }
  .fp-pin .no { color:#d29922; }
  .fp-mini { font-size:0.875rem; padding:2px 7px; border-radius:6px; border:1px solid #1f6feb88; color:#79c0ff; background:transparent; cursor:pointer; margin-left:4px; }
  .fp-form { display:flex; flex-wrap:wrap; gap:6px; align-items:center; margin:6px 0; font-size:0.875rem; color:#c9d1d9; }
  .fp-form input { font-size:0.875rem; padding:3px 6px; border-radius:6px; border:1px solid #30363d; background:#0d1117; color:#e6edf3; }
  .fp-form input[data-motivo] { min-width:180px; flex:1; }
  .fp-curso { padding:4px 0 8px; border-bottom:1px solid #21262d; }
  .fp-escala { display:flex; align-items:center; flex-wrap:wrap; gap:6px; margin-top:8px; }
  .fp-peldano { font-size:0.875rem; font-weight:700; padding:5px 12px; border-radius:7px; border:1px solid #30363d; color:#6e7681; }
  .fp-peldano.hecho { color:#3fb950; border-color:#3fb95066; }
  .fp-peldano.actual { color:#0d1117; background:#79c0ff; border-color:#79c0ff; }
  .fp-flecha { color:#484f58; font-size:0.875rem; }
  .fp-cls.ok { color:#3fb950; border-color:#3fb95055; } .fp-cls.tar { color:#d29922; border-color:#d2992255; } .fp-cls.nov { color:#f85149; border-color:#f8514955; }
`;

function cabeceraHtml(p) {
  const c = cabeceraDeLaPersona(p);
  return `
    <div class="fp-cabeza">
      <div class="fp-avatar">${esc(c.iniciales)}</div>
      <div style="flex:1;min-width:220px">
        <h1 class="fp-nombre" data-ficha-nombre>${esc(c.nombre)}</h1>
        <div class="fp-chips">${c.chips.map((x) => `<span class="fp-chip${x.ok ? ' ok' : ''}">${esc(x.texto)}</span>`).join('')}</div>
      </div>
    </div>`;
}

function tarjetasHtml(p) {
  return `<div class="fp-grid">${tarjetasDelMes(p).map((t) => `
    <div class="fp-card${t.apagada ? ' apagada' : ''}" data-tarjeta="${esc(t.clave)}">
      <div class="t">${esc(t.titulo)}</div>
      <div class="v">${esc(t.valor)}</div>
      <div class="d">${esc(t.detalle)}</div>
      <span class="f">${esc(t.fuente)}</span>
      ${t.falta ? `<div class="x">${esc(t.falta)}</div>` : ''}
      ${t.abre ? `<details class="fp-abre" data-abre="${esc(t.clave)}"><summary>Ver el detalle</summary><div data-detalle></div></details>` : ''}
    </div>`).join('')}</div>`;
}

// ── SUJ-T8 bloque 2: el detalle de cada tarjeta, pedido SOLO al abrirla (una vez) ────────────────

const hoyIso = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => { const t = new Date(); t.setDate(t.getDate() - n); return t.toISOString().slice(0, 10); };
const fila = (celdas) => `<tr>${celdas.map((c, i) => `<td${i >= celdas.length - 1 ? ' class="n"' : ''}>${c}</td>`).join('')}</tr>`;
const sinDetalle = (falta) => `<p class="fp-nada" data-detalle-motivo="${esc(falta.motivo)}">${esc(falta.frase)}</p>`;

async function detalleDeAsistencia(p) {
  const fichas = fichasPorNegocio(p);
  if (!fichas.length) return '<p class="fp-nada">No tiene fichas de operación.</p>';
  const partes = await Promise.all(fichas.map(async (f) => {
    const res = await AsistenciaApiClient.anomalias(f.businessId, { desde: haceDias(30), hasta: hoyIso() });
    const falta = porQueNoHayDetalle(res);
    if (falta) return `<div class="fp-sub">${esc(f.negocio)}</div>${sinDetalle(falta)}`;
    const dias = diasDeAsistencia(filasDelColaborador(filasDelDetector(res), { id: f.colaboradorId, nombreCompleto: p.nombre }));
    if (!dias.length) return `<div class="fp-sub">${esc(f.negocio)}</div><p class="fp-nada">Sin marcajes en 30 días.</p>`;
    // SUJ-T8 bloque 3: -Corregir- solo para quien la API deja corregir (admin o manager).
    const corrige = puedeCorregir(AppConfig.role);
    return `<div class="fp-sub">${esc(f.negocio)}</div><table class="fp-det">${dias.map((d) =>
      fila([esc(d.dia), `<span class="fp-cls ${d.clase === 'OK' ? 'ok' : d.clase === 'TARDANZA' ? 'tar' : 'nov'}">${esc(claseEnPalabras(d.clase, d.desvio))}</span>`,
        esc(d.entrada || '—') + (corrige ? ` <button type="button" class="fp-mini" data-corregir data-negocio="${esc(f.businessId)}" data-colaborador="${esc(f.colaboradorId)}" data-dia="${esc(d.orden)}">Corregir</button>` : '')])).join('')}</table>`;
  }));
  return partes.join('') + '<p class="fp-nada">La clasificación es la del detector, la misma de la cuadrícula de Asistencia.'
    + (puedeCorregir(AppConfig.role) ? ' «Corregir» conserva la hora que se marcó y deja el motivo y quién corrigió. Agregar un marcaje que faltó todavía no se puede desde aquí.' : '') + '</p>';
}

/** SUJ-T8 bloque 3: los marcajes de ESE dia, pedidos al tocar -Corregir-, con su formulario. */
async function abrirCorreccion($btn) {
  const $fila = $btn.closest('tr');
  if (!$fila || $fila.nextElementSibling?.classList.contains('fp-corr')) return;
  const { negocio, colaborador, dia } = $btn.dataset;
  const $tr = document.createElement('tr');
  $tr.className = 'fp-corr';
  $tr.innerHTML = `<td colspan="3"><p class="fp-nada">Cargando los marcajes del día…</p></td>`;
  $fila.after($tr);
  const siguiente = new Date(Date.parse(`${dia}T12:00:00Z`) + 86400000).toISOString().slice(0, 10);
  const res = await AsistenciaApiClient.list(negocio, { colaboradorId: colaborador, fechaDesde: dia, fechaHasta: siguiente, pageSize: 20 });
  const falta = porQueNoHayDetalle(res);
  const $td = $tr.firstElementChild;
  if (falta) { $td.innerHTML = sinDetalle(falta); return; }
  // Solo los marcajes cuyo dia en Colombia es ESE dia (el listado va hasta el dia siguiente).
  const marcas = marcasDelDia(res).filter((m) => diaBogota(m.marcado) === dia);
  if (!marcas.length) { $td.innerHTML = '<p class="fp-nada">No hay marcajes ese día para corregir.</p>'; return; }
  $td.innerHTML = marcas.map((m) => `
    <div class="fp-form" data-marca="${esc(m.id)}" data-marcado="${esc(m.marcado)}" data-dia="${esc(dia)}" data-negocio="${esc(negocio)}">
      <span>${esc(m.tipo)} · marcó ${esc(horaBogota(m.marcado))}${m.corregido ? ` · ya corregido a ${esc(horaBogota(m.corregido))}` : ''}</span>
      <input type="time" data-hora value="${esc(horaBogota(m.corregido || m.marcado))}" aria-label="Hora corregida">
      <input type="text" data-motivo placeholder="Motivo (obligatorio)" aria-label="Motivo">
      <button type="button" class="fp-mini" data-guardar-correccion>Guardar</button>
      <span class="fp-nada" data-resultado></span>
    </div>`).join('');
}

async function guardarCorreccion($btn) {
  const $f = $btn.closest('[data-marca]');
  const $res = $f.querySelector('[data-resultado]');
  const r = correccionDe({ dia: $f.dataset.dia, hora: $f.querySelector('[data-hora]').value, motivo: $f.querySelector('[data-motivo]').value, marcado: $f.dataset.marcado });
  if (!r.ok) { $res.textContent = r.error; return; }
  $btn.disabled = true;
  const res = await AsistenciaApiClient.correccion($f.dataset.negocio, $f.dataset.marca, r.cuerpo);
  $btn.disabled = false;
  $res.textContent = res.ok
    ? `Corregida a ${horaBogota(r.cuerpo.timestampCorregido)}. La hora marcada se conserva y queda quién corrigió.`
    : (porQueNoHayDetalle(res)?.frase || 'La API no aceptó la corrección.');
}

async function detalleDeDanos(p) {
  const res = await PersonaApiClient.danos(p.personaId);
  const falta = porQueNoHayDetalle(res);
  if (falta) return sinDetalle(falta);
  const negocioDe = (id) => (p.membresias.find((m) => m.businessId === id)?.negocio || id);
  const { filas, total } = danosPorDia(res, negocioDe);
  if (!filas.length) return '<p class="fp-nada">No tiene parte en daños este mes operativo.</p>';
  const varios = new Set(filas.map((f) => f.negocio)).size > 1;
  return `<table class="fp-det">${filas.map((f) => fila([
    esc(f.dia), ...(varios ? [esc(f.negocio)] : []), esc(f.insumo),
    `${esc(f.cantidad.toLocaleString('es-CO'))}${f.entre > 1 ? ` · entre ${f.entre}` : ''}`, esc(pesosCOP(f.suParte))])).join('')}</table>
    <p class="fp-nada">Su parte: ${esc(pesosCOP(total))}. Cada daño se reparte entre quienes trabajaron ese día en el área; quien cuenta solo firma.</p>`;
}

async function detalleDeCreditos(p, $card) {
  const res = await PersonaApiClient.creditos(p.personaId);
  const falta = porQueNoHayDetalle(res);
  if (falta) return sinDetalle(falta);
  const negocioDe = (id) => (p.membresias.find((m) => m.businessId === id)?.negocio || id);
  const c = creditosPorNegocio(res, negocioDe);
  const t = titularDeCreditos(c);
  // El titular de la tarjeta se llena con lo que llego (antes decia "se consulta al abrir").
  const $v = $card.querySelector('.v'); if ($v) $v.textContent = t.valor;
  const $d = $card.querySelector('.d'); if ($d) $d.textContent = t.detalle;
  const conSaldo = c.negocios.flatMap((n) => n.creditos.filter((x) => x.saldo > 0 || x.sobrepago).map((x) => ({ ...x, negocio: n.negocio })));
  if (!conSaldo.length) return '<p class="fp-nada">Sin créditos con saldo.</p>';
  return `<table class="fp-det">${conSaldo.map((x) => fila([
    esc(x.fecha), esc(x.factura || '—'),
    `${esc(pesosCOP(x.total))}${x.abonos.length ? ` · ${x.abonos.length} abono${x.abonos.length === 1 ? '' : 's'}` : ''}${x.posible ? ' · <span class="fp-cls tar">posible</span>' : ''}${x.sobrepago ? ' · <span class="fp-cls nov">abonos de más, no suma</span>' : ''}`,
    `debe ${esc(pesosCOP(x.saldo))}`])).join('')}</table>
    <p class="fp-nada">Confirmado = cruza por documento o nombre. Posible = solo por teléfono.</p>`;
}

async function abrirDetalle($det, p) {
  if ($det.dataset.cargado) return;
  $det.dataset.cargado = '1';
  const $cuerpo = $det.querySelector('[data-detalle]');
  $cuerpo.innerHTML = '<p class="fp-nada">Cargando…</p>';
  const clave = $det.dataset.abre;
  try {
    $cuerpo.innerHTML = clave === 'asistencia' ? await detalleDeAsistencia(p)
      : clave === 'danos' ? await detalleDeDanos(p)
      : clave === 'creditos' ? await detalleDeCreditos(p, $det.closest('.fp-card'))
      : '';
  } catch {
    delete $det.dataset.cargado;
    $cuerpo.innerHTML = '<p class="fp-nada">No se pudo consultar. Ciérralo y ábrelo otra vez.</p>';
  }
}

// ── VIS-T3: como va mes a mes (los ultimos 6), para ver si ESTA persona mejora; nunca compara con otra ────────────────

const hoyBogota = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10);

async function detalleMesAMes(p) {
  const fichas = fichasPorNegocio(p);
  if (!fichas.length) return '<p class="fp-nada">No tiene fichas de operación: no hay meses que mostrar.</p>';
  const meses = mesesHacia(hoyBogota(), MESES_MES_A_MES);
  // Una consulta para daños y arqueos (todas las fichas) y una por mes y negocio para la asistencia del detector.
  const [api, ...porFicha] = await Promise.all([
    PersonaApiClient.mesAMes(p.personaId, MESES_MES_A_MES).then(leerMesAMes),
    ...fichas.map((f) => Promise.all(meses.map((m) => AsistenciaApiClient.anomalias(f.businessId, { desde: m.desde, hasta: m.hasta })))),
  ]);
  const partes = fichas.map((f, i) => {
    const asistencias = porFicha[i].map((res) => (res && res.ok
      ? asistenciaDelMes(filasDelColaborador(filasDelDetector(res), { id: f.colaboradorId, nombreCompleto: p.nombre }))
      : null));
    const t = tablaDelNegocio(meses, asistencias, api, f.businessId);
    if (!t.columnas.length) return `<div class="fp-sub">${esc(f.negocio)}</div><p class="fp-nada" data-mes-vacio>Sin datos en los últimos ${MESES_MES_A_MES} meses.</p>`;
    return `<div class="fp-sub">${esc(f.negocio)}</div><table class="fp-det fp-mes" data-mes-a-mes-tabla>
      <thead><tr><th>Mes</th>${t.columnas.map((c) => `<th>${esc(TITULOS[c])}</th>`).join('')}</tr></thead>
      <tbody>${t.filas.map((r) => `<tr data-mes="${esc(r.mes)}"><td>${esc(r.nombre)}</td>${t.columnas.map((c) =>
        `<td data-l="${esc(TITULOS[c])}">${esc(textoDeCelda(c, r[c], pesosCOP))}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
  });
  const aviso = avisoDeLaApi(api);
  return partes.join('') + (aviso ? `<p class="fp-nada" data-mes-aviso>${esc(aviso)}</p>` : '')
    + '<p class="fp-nada">Se lee de arriba abajo para ver si mejora. La asistencia es la del detector, la misma de la cuadrícula de Asistencia.</p>';
}

// ── SUJ-T8 bloque 4: Cursando ahora, por programa (escala del tutor o quiz del panel) ───────────

const listaDe = (res) => { const d = res?.data?.data ?? res?.data ?? []; return Array.isArray(d) ? d : []; };

function escalaHtml(x) {
  const peldanos = x.peldanos.map((s) => `<span class="fp-peldano ${s.estado}" data-peldano="${esc(s.nivel)}">${esc(s.nivel)}</span>`).join('<span class="fp-flecha">→</span>');
  const tema = x.tema ? `Tema en curso: ${esc(x.tema)}${x.temaN ? ` (${x.temaN} de ${x.temasTotal})` : ''}` : 'Sin tema en curso';
  const dominio = x.dominioEstimado === null ? '' : ` · dominio ${x.dominioEstimado}% (lo estima el tutor, sin confirmar)`;
  return `
    <div class="fp-curso" data-curso="${esc(x.programa)}" data-piel="escala">
      <div class="fp-sub">${esc(x.nombre)}</div>
      <div class="fp-escala">${peldanos}</div>
      <p class="fp-nada">${x.nivelConocido ? `Va en ${esc(x.nivel)}.` : `Nivel sin dato en su perfil${x.nivel ? ` (${esc(x.nivel)})` : ''}.`} ${tema}${esc(dominio)}.</p>
      <p class="fp-nada">${x.temasTotal ? `${x.dominados} de ${x.temasTotal} temas dominados` : `${x.dominados} temas dominados`} · ${x.sesiones} ${x.sesiones === 1 ? 'sesión' : 'sesiones'} con el tutor${x.ultimaSesion ? ` · última ${esc(cuandoCorto(x.ultimaSesion))}` : ''}${x.validado ? '' : ' · perfil sin validar'}</p>
    </div>`;
}

function quizHtml(x) {
  return `
    <div class="fp-curso" data-piel="quiz">
      <div class="fp-sub">${esc(x.nombre)}</div>
      <p class="fp-nada">${x.respuestas.toLocaleString('es-CO')} respuestas · ${x.correctas.toLocaleString('es-CO')} bien (${x.aciertoPct}%)${x.ultima ? ` · última ${esc(cuandoCorto(x.ultima))}` : ''}</p>
      <p class="fp-nada">${esc(x.nota)}</p>
    </div>`;
}

const cuandoCorto = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) ? formatearFecha(new Date(t), { timeZone: 'America/Bogota', day: 'numeric', month: 'short' }, { parte: 'todo' }) : ''; };

async function detalleDeCursando(p) {
  const programas = programasDelPensum(await LearningApiClient.getPensum());
  const respuestas = await Promise.all(programas.map(async (programa) => ({ programa, r: await LearningProfileApiClient.getProfile({ sujeto: p.personaId, programa: programa.codigo }) })));
  const conPerfil = respuestas.filter((x) => x.r.ok && x.r.perfil);
  for (const x of conPerfil) {
    const actual = skillActual(x.programa, x.r.perfil);
    const temas = actual ? listaDe(await LearningApiClient.getTopics(actual)) : [];
    x.temas = temas.map((t) => t.code ?? t.Code).filter(Boolean);
  }
  const items = cursandoAhora({ perfiles: conPerfil, capacitacion: p.capacitacion });
  if (!items.length) {
    const fallos = respuestas.filter((x) => !x.r.ok).map((x) => x.r);
    const falta = porQueNoCursaNada({ fallos, sinPerfil: programas.length });
    return `<p class="fp-nada" data-cursando-motivo="${esc(falta.motivo)}">${esc(falta.frase)}</p>`;
  }
  return items.map((x) => (x.piel === 'escala' ? escalaHtml(x) : quizHtml(x))).join('');
}

// FICHA-PERSONA-AGENTE-T1 (Ares #33993): donde vive cada cosa de la persona. No copia su avance: enlaza a la vista que
// lo tiene. Las vistas abren SIN filtrar por esta persona (todavia no reciben a quien); se dice para que nadie lo espere.
const DONDE_ESTA = [
  ['Capacitación', '#/capacitacion', 'programas y su avance'],
  ['Insignias', '#/skills', 'lo que ya tiene reconocido'],
  ['Pensum', '#/pensum', 'unidades aprobadas y verificadas'],
  ['NXS', '#/banco', 'saldo y movimientos']
];
function dondeEstaHtml() {
  const items = DONDE_ESTA.map(([t, ruta, que]) =>
    `<li data-fp-donde="${esc(ruta)}"><a href="${esc(ruta)}" style="color:#58a6ff">${esc(t)}</a> <span class="fp-nada" style="display:inline">· ${esc(que)}</span></li>`).join('');
  return `<details class="fp-negocio" data-fp-donde-esta><summary>Dónde está cada cosa</summary>
    <p class="fp-nada">Cada enlace abre la vista completa, sin filtrar por esta persona: allí se busca.</p>
    <ul style="margin:6px 0 0;padding-left:18px;font-size:0.875rem;line-height:1.8">${items}
      <li data-fp-donde="arcade"><span style="color:#8b949e">Arcade</span> <span class="fp-nada" style="display:inline">· el juego no tiene ruta en el panel</span></li>
    </ul></details>`;
}

// SUJ-T13 puntos 1 y 3: el PIN de cada ficha de operacion (si lo tiene, nunca cual) y para que sirve.
// SUJ-T13 punto 2: el negocio donde entra por panel y no tiene ficha ofrece crearla (manager/admin).
function pinesHtml(p) {
  const fichas = fichasPorNegocio(p);
  const sinFicha = negociosSinFicha(p);
  if (!fichas.length && !sinFicha.length) return '';
  const crea = puedeCambiarPin(AppConfig.role);
  return `
    <div class="fp-h2">PIN de operación</div>
    ${fichas.map((f) => { const l = lineaDePin(f, AppConfig.role); return `
      <div class="fp-pin" data-pin-negocio="${esc(f.businessId)}">
        <span>${esc(f.negocio)}</span> <span class="${l.tiene ? '' : 'no'}" data-pin-estado>${esc(l.estado)}</span>
        ${l.boton ? `<button type="button" class="fp-mini" data-cambiar-pin data-negocio="${esc(f.businessId)}" data-negocio-nombre="${esc(f.negocio)}" data-colaborador="${esc(f.colaboradorId)}">${esc(l.boton)}</button>` : ''}
      </div>`; }).join('')}
    ${sinFicha.map((n) => `
      <div class="fp-pin" data-sin-ficha="${esc(n.businessId)}">
        <span>${esc(n.negocio)}</span> <span class="no">Sin ficha de operación</span>
        ${crea ? `<button type="button" class="fp-mini" data-crear-ficha data-negocio="${esc(n.businessId)}">Crear ficha de operación</button>` : ''}
      </div>`).join('')}
    <p class="fp-nada" data-pin-para-que>${esc(PARA_QUE_SIRVE_EL_PIN)}${sinFicha.length ? ' Sin ficha de operación en un negocio, la persona no tiene PIN ni marca ahí.' : ''}</p>`;
}

/** SUJ-T13 punto 2: el formulario debajo de la fila. El cargo va vacio a proposito (Q #37151). */
function abrirCrearFicha($btn) {
  const $fila = $btn.closest('[data-sin-ficha]');
  if ($fila.nextElementSibling?.matches('[data-form-ficha]')) return;
  const $f = document.createElement('div');
  $f.className = 'fp-form';
  $f.dataset.formFicha = $btn.dataset.negocio;
  $f.innerHTML = `
    <input type="text" data-documento placeholder="Documento" aria-label="Documento" maxlength="30">
    <input type="text" data-cargo placeholder="Cargo (obligatorio)" aria-label="Cargo" maxlength="100">
    <input type="password" data-pin inputmode="numeric" maxlength="6" placeholder="PIN (opcional)" aria-label="PIN">
    <input type="password" data-pin-confirma inputmode="numeric" maxlength="6" placeholder="Repite el PIN" aria-label="Repite el PIN">
    <button type="button" class="fp-mini" data-guardar-ficha>Crear</button>
    <span class="fp-nada" style="flex-basis:100%" data-aviso-cargo>${esc(AVISO_DEL_CARGO)}</span>
    <span class="fp-nada" style="flex-basis:100%" data-resultado-ficha></span>`;
  $fila.after($f);
  $f.querySelector('[data-documento]').focus();
}

async function guardarFicha($btn, p, container) {
  const $f = $btn.closest('[data-form-ficha]');
  const $res = $f.querySelector('[data-resultado-ficha]');
  const v = cuerpoDeLaFicha({
    businessId: $f.dataset.formFicha,
    documento: $f.querySelector('[data-documento]').value,
    cargo: $f.querySelector('[data-cargo]').value,
    pin: $f.querySelector('[data-pin]').value,
    confirmacion: $f.querySelector('[data-pin-confirma]').value
  });
  if (!v.ok) { $res.textContent = v.error; return; }
  $btn.disabled = true;
  const r = fraseDelResultado(await PersonaApiClient.crearFichaDeOperacion(p.personaId, v.cuerpo));
  $btn.disabled = false;
  $res.textContent = r.frase;
  // Creada: la ficha se vuelve a leer de la API (la fila pasa a "Tiene PIN" / "Sin PIN" con su boton).
  if (r.ok) await renderFichaPersona(container);
}

// COLABORADOR-EDITAR-NOMBRE-T1: corregir nombre, documento y telefono de la ficha de cada negocio, con el mismo
// PUT de la lista de Colaboradores. Lo puede quien puede cambiar el PIN (manager o admin).
function datosHtml(p) {
  const fichas = fichasPorNegocio(p);
  if (!fichas.length || !puedeCambiarPin(AppConfig.role)) return '';
  return `
    <div class="fp-h2">Nombre y contacto</div>
    ${fichas.map((f) => `
      <div class="fp-pin" data-datos-negocio="${esc(f.businessId)}">
        <span>${esc(f.negocio)}</span>
        <button type="button" class="fp-mini" data-editar-datos data-negocio="${esc(f.businessId)}" data-colaborador="${esc(f.colaboradorId)}">Editar datos</button>
      </div>`).join('')}`;
}

/** El formulario debajo de la fila, con lo que hoy tiene la ficha en ese negocio. */
async function abrirDatos($btn) {
  const $fila = $btn.closest('[data-datos-negocio]');
  if ($fila.nextElementSibling?.matches('[data-form-datos]')) return;
  $btn.disabled = true;
  const res = await ColaboradorApiClient.getById($btn.dataset.negocio, $btn.dataset.colaborador);
  $btn.disabled = false;
  const $f = document.createElement('div');
  $f.className = 'fp-form';
  $f.dataset.formDatos = $btn.dataset.negocio;
  $f.dataset.colaborador = $btn.dataset.colaborador;
  if (!res.ok) {
    $f.innerHTML = `<span class="fp-nada" data-resultado-datos>No se pudo leer la ficha en este negocio: ${esc(res.error || 'sin respuesta de la API')}</span>`;
    $fila.after($f);
    return;
  }
  const d = datosDelColaborador(res.data?.data || res.data);
  $f.innerHTML = `
    <input type="text" data-dato="nombre" value="${esc(d.nombre)}" placeholder="Nombre y apellido" aria-label="Nombre" maxlength="200">
    <input type="text" data-dato="documento" value="${esc(d.documento)}" placeholder="Documento" aria-label="Documento" maxlength="30">
    <input type="tel" data-dato="telefono" value="${esc(d.telefono)}" placeholder="Teléfono" aria-label="Teléfono" maxlength="30">
    <button type="button" class="fp-mini" data-guardar-datos>Guardar</button>
    <span class="fp-nada" style="flex-basis:100%" data-resultado-datos></span>`;
  $fila.after($f);
  $f.querySelector('[data-dato="nombre"]').focus();
}

async function guardarDatos($btn, container) {
  const $f = $btn.closest('[data-form-datos]');
  const $res = $f.querySelector('[data-resultado-datos]');
  const valor = (k) => $f.querySelector(`[data-dato="${k}"]`).value;
  const v = cuerpoDeDatos({ nombre: valor('nombre'), documento: valor('documento'), telefono: valor('telefono') });
  if (!v.ok) {
    $res.textContent = v.error;
    $f.querySelector(`[data-dato="${v.campo}"]`).focus();
    return;
  }
  $btn.disabled = true;
  const r = fraseDeDatos(await ColaboradorApiClient.update($f.dataset.formDatos, $f.dataset.colaborador, v.cuerpo));
  $btn.disabled = false;
  $res.textContent = r.frase;
  // Guardado: la ficha se vuelve a leer de la API (la cabecera toma el nombre de la persona).
  if (r.ok) await renderFichaPersona(container);
}

function negociosHtml(p) {
  const fichas = fichasPorNegocio(p);
  if (!fichas.length) return '';
  return `
    <div class="fp-h2">Por negocio · se carga al abrir</div>
    ${fichas.map((f) => `
      <details class="fp-negocio" data-negocio="${esc(f.businessId)}" data-colaborador="${esc(f.colaboradorId)}">
        <summary>${esc(f.negocio)} · asistencia, créditos y capacitación</summary>
        <div data-negocio-cuerpo></div>
      </details>`).join('')}`;
}

async function abrirNegocio($det) {
  if ($det.dataset.cargado) return;
  $det.dataset.cargado = '1';
  const $cuerpo = $det.querySelector('[data-negocio-cuerpo]');
  const businessId = $det.dataset.negocio;
  const res = await ColaboradorApiClient.getById(businessId, $det.dataset.colaborador);
  if (!res.ok) {
    $cuerpo.innerHTML = `<p class="empty-state" style="color:#f85149">No se pudo leer la ficha en este negocio: ${esc(res.error || 'sin respuesta de la API')}</p>`;
    delete $det.dataset.cargado;
    return;
  }
  const colaborador = res.data?.data || res.data;
  const { renderFichaColaborador } = await import('../shared/NxFichaColaborador.js');
  await renderFichaColaborador($cuerpo, { businessId, colaborador, soloOperacion: true });
}

/**
 * Q #17401: una ficha de colaborador SIN persona (inactivos viejos, y todo colaborador nuevo
 * hasta SUJ-T10) no se puede quedar sin ficha. Se muestra la ficha completa del colaborador,
 * la de antes, en el negocio elegido, con una linea que dice que aun no tiene persona.
 */
async function fichaSinPersona($f, colaboradorId, frase) {
  const businessId = getSelectedBusiness();
  const res = businessId ? await ColaboradorApiClient.getById(businessId, colaboradorId) : null;
  if (!res || !res.ok) {
    $f.innerHTML = `<p class="empty-state" data-ficha-motivo="SIN_PERSONA">${esc(frase)}</p>`;
    return;
  }
  const colaborador = res.data?.data || res.data;
  $f.innerHTML = `<p style="font-size:0.875rem;color:#d29922;margin:10px 0" data-ficha-sin-persona>${esc(frase)} Se muestra su ficha de colaborador en este negocio.</p><div data-ficha-colaborador></div>`;
  const { renderFichaColaborador } = await import('../shared/NxFichaColaborador.js');
  await renderFichaColaborador($f.querySelector('[data-ficha-colaborador]'), { businessId, colaborador });
}

function consultaDe(ruta) {
  if (ruta.tipo === 'colaborador') return PersonaApiClient.porColaborador(ruta.id);
  if (ruta.tipo === 'usuario') return PersonaApiClient.porUsuario(ruta.id);
  return PersonaApiClient.obtener(ruta.id);
}

export async function renderFichaPersona(container) {
  const ruta = leerRutaDeLaFicha(window.location.hash);
  container.innerHTML = `
    <style>${CSS}</style>
    <section class="artifact-view fp">
      ${regresoHtml({ volverA: "javascript:history.back()", texto: "Volver" })}
      <div id="nx-ficha-persona"><p class="empty-state">Cargando la ficha…</p></div>
    </section>`;
  const $f = container.querySelector('#nx-ficha-persona');
  if (!ruta) {
    $f.innerHTML = '<p class="empty-state">Esta dirección no es de una ficha.</p>';
    return;
  }

  const res = await consultaDe(ruta);
  const p = leerPersona(res);
  if (!p) {
    const falta = porQueNoHayPersona(res) || { motivo: 'VACIA', frase: 'La API respondió sin persona.' };
    if (ruta.tipo === 'colaborador' && falta.motivo === 'SIN_PERSONA') {
      await fichaSinPersona($f, ruta.id, falta.frase);
      return;
    }
    $f.innerHTML = `<p class="empty-state" data-ficha-motivo="${esc(falta.motivo)}">${esc(falta.frase)}</p>`;
    return;
  }

  $f.innerHTML = cabeceraHtml(p)
    + '<div class="fp-h2">Cómo va este mes</div>'
    + tarjetasHtml(p)
    + '<details class="fp-negocio" data-mes-a-mes><summary>Cómo va mes a mes · últimos 6 meses, se carga al abrir</summary><div data-mes-cuerpo></div></details>'
    + '<details class="fp-negocio" data-cursando><summary>Cursando ahora · por programa, se carga al abrir</summary><div data-cursando-cuerpo></div></details>'
    + dondeEstaHtml()
    + datosHtml(p)
    + pinesHtml(p)
    + negociosHtml(p);

  // SUJ-T8 bloque 4: una vez, al abrir.
  const $curso = $f.querySelector('details[data-cursando]');
  $curso.addEventListener('toggle', async () => {
    if (!$curso.open || $curso.dataset.cargado) return;
    $curso.dataset.cargado = '1';
    const $c = $curso.querySelector('[data-cursando-cuerpo]');
    $c.innerHTML = '<p class="fp-nada">Cargando…</p>';
    try { $c.innerHTML = await detalleDeCursando(p); }
    catch { delete $curso.dataset.cargado; $c.innerHTML = '<p class="fp-nada">No se pudo consultar. Ciérralo y ábrelo otra vez.</p>'; }
  });

  // VIS-T3: mes a mes, una vez, al abrir (6 consultas de asistencia por negocio y una de daños y arqueos).
  const $mes = $f.querySelector('details[data-mes-a-mes]');
  $mes.addEventListener('toggle', async () => {
    if (!$mes.open || $mes.dataset.cargado) return;
    $mes.dataset.cargado = '1';
    const $c = $mes.querySelector('[data-mes-cuerpo]');
    $c.innerHTML = '<p class="fp-nada">Cargando…</p>';
    try { $c.innerHTML = await detalleMesAMes(p); }
    catch { delete $mes.dataset.cargado; $c.innerHTML = '<p class="fp-nada">No se pudo consultar. Ciérralo y ábrelo otra vez.</p>'; }
  });

  // Solo los de un negocio: "Cursando" y "Dónde está cada cosa" comparten la clase pero no son fichas de colaborador.
  $f.querySelectorAll('details.fp-negocio[data-negocio]').forEach(($d) => {
    $d.addEventListener('toggle', () => { if ($d.open) abrirNegocio($d); });
  });
  // SUJ-T8 bloque 2: cada tarjeta pide su detalle al abrirse, una vez.
  $f.querySelectorAll('details.fp-abre').forEach(($d) => {
    $d.addEventListener('toggle', () => { if ($d.open) abrirDetalle($d, p); });
  });
  // SUJ-T8 bloque 3: -Corregir- y -Guardar- viven dentro del detalle que se pinta despues.
  $f.addEventListener('click', (e) => {
    // SUJ-T13 punto 2: crear la ficha de operacion donde entra por panel.
    const $crear = e.target.closest('[data-crear-ficha]');
    if ($crear) { abrirCrearFicha($crear); return; }
    const $guardarFicha = e.target.closest('[data-guardar-ficha]');
    if ($guardarFicha) { guardarFicha($guardarFicha, p, container); return; }
    // COLABORADOR-EDITAR-NOMBRE-T1: nombre, documento y telefono de la ficha de ese negocio.
    const $editar = e.target.closest('[data-editar-datos]');
    if ($editar) { abrirDatos($editar); return; }
    const $guardarDatos = e.target.closest('[data-guardar-datos]');
    if ($guardarDatos) { guardarDatos($guardarDatos, container); return; }
    // SUJ-T13: el mismo modal de Colaboradores, con el negocio de ESA ficha (no el del selector).
    const $pin = e.target.closest('[data-cambiar-pin]');
    if ($pin) {
      const { negocio, negocioNombre, colaborador } = $pin.dataset;
      import('../shared/NxModalDePin.js').then(({ abrirModalDePin }) => abrirModalDePin({
        businessId: negocio, colaboradorId: colaborador, nombre: p.nombre, negocio: negocioNombre,
        alGuardar: () => { const $e = $pin.parentElement.querySelector('[data-pin-estado]'); $e.textContent = 'Tiene PIN'; $e.className = ''; $pin.textContent = 'Cambiar PIN'; }
      }));
      return;
    }
    const $c = e.target.closest('[data-corregir]');
    if ($c) { abrirCorreccion($c); return; }
    const $g = e.target.closest('[data-guardar-correccion]');
    if ($g) guardarCorreccion($g);
  });
}
